import type { Fest, FestSeason, FestStatus } from "../types";

export const WINDOW_DAYS = 120;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function startOfLocalDay(from = new Date()): Date {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate());
}

export function windowEnd(from = new Date(), days = WINDOW_DAYS): Date {
  const start = startOfLocalDay(from);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + days);
}

function typicalForYear(fest: Fest, year: number): FestSeason {
  const t = fest.typical;
  const endYear = t.endMonth < t.startMonth ? year + 1 : year;
  return {
    start: `${year}-${pad(t.startMonth)}-${pad(t.startDay)}`,
    end: `${endYear}-${pad(t.endMonth)}-${pad(t.endDay)}`,
    cadence: t.cadence,
    notes: t.notes,
  };
}

/** Dated seasons plus a typical projection so the calendar does not go empty. */
export function allSeasons(fest: Fest, from = new Date()): FestSeason[] {
  const year = from.getFullYear();
  const known = [...fest.seasons].sort((a, b) => a.start.localeCompare(b.start));
  const last = known[known.length - 1];
  const extra: FestSeason[] = [];
  if (!last || parseIso(last.end) < from) {
    extra.push(typicalForYear(fest, year));
    extra.push(typicalForYear(fest, year + 1));
  } else if (parseIso(last.end).getFullYear() <= year) {
    extra.push(typicalForYear(fest, year + 1));
  }
  const seen = new Set(known.map((s) => s.start));
  for (const season of extra) {
    if (!seen.has(season.start)) known.push(season);
  }
  return known.sort((a, b) => a.start.localeCompare(b.start));
}

export function currentSeason(fest: Fest, from = new Date()): FestSeason | null {
  const day = startOfLocalDay(from);
  return (
    allSeasons(fest, from).find((season) => {
      return parseIso(season.start) <= day && parseIso(season.end) >= day;
    }) ?? null
  );
}

export function nextSeason(fest: Fest, from = new Date()): FestSeason | null {
  const day = startOfLocalDay(from);
  const current = currentSeason(fest, from);
  if (current) return current;
  return (
    allSeasons(fest, from).find((season) => parseIso(season.end) >= day) ?? null
  );
}

export function festStatus(fest: Fest, from = new Date()): FestStatus {
  const day = startOfLocalDay(from);
  const through = windowEnd(from);
  if (currentSeason(fest, from)) return "now";
  const next = nextSeason(fest, from);
  if (!next) return "past";
  const start = parseIso(next.start);
  if (start <= through) return "upcoming";
  if (start > day) return "later";
  return "past";
}

export function cadenceLabel(cadence: FestSeason["cadence"]): string {
  if (cadence === "weekends") return "Weekends";
  if (cadence === "daily") return "Daily";
  if (cadence === "year-round") return "Year-round";
  return "One run";
}

export function formatDay(iso: string): string {
  return parseIso(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function formatRange(start: string, end: string): string {
  const a = parseIso(start);
  const b = parseIso(end);
  const sameYear = a.getFullYear() === b.getFullYear();
  const sameMonth = sameYear && a.getMonth() === b.getMonth();
  if (start === end) {
    return a.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  if (sameMonth) {
    return `${a.toLocaleDateString("en-US", { month: "short", day: "numeric" })}–${b.getDate()}, ${b.getFullYear()}`;
  }
  const startFmt = a.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
  const endFmt = b.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startFmt} – ${endFmt}`;
}

export interface FestWindow {
  fest: Fest;
  season: FestSeason;
  status: FestStatus;
}

export function festsInWindow(
  fests: Fest[],
  from = new Date(),
  days = WINDOW_DAYS,
): FestWindow[] {
  const day = startOfLocalDay(from);
  const through = windowEnd(from, days);
  const rows: FestWindow[] = [];
  for (const fest of fests) {
    for (const season of allSeasons(fest, from)) {
      const start = parseIso(season.start);
      const end = parseIso(season.end);
      if (end < day || start > through) continue;
      const status: FestStatus =
        start <= day && end >= day ? "now" : "upcoming";
      rows.push({ fest, season, status });
    }
  }
  return rows.sort((a, b) => {
    if (a.status !== b.status) return a.status === "now" ? -1 : 1;
    return a.season.start.localeCompare(b.season.start);
  });
}

export function groupByMonth(rows: FestWindow[]): Array<{
  label: string;
  key: string;
  rows: FestWindow[];
}> {
  const map = new Map<string, FestWindow[]>();
  for (const row of rows) {
    const d = parseIso(row.season.start);
    const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    const list = map.get(key) ?? [];
    list.push(row);
    map.set(key, list);
  }
  return [...map.entries()].map(([key, groupRows]) => {
    const [y, m] = key.split("-").map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    return { label, key, rows: groupRows };
  });
}
